'use client';
import { useEffect } from 'react';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';

export default function RefundPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <SectionHeader
              eyebrow="Legal"
              title="Refund Policy"
              description="Last updated: February 18, 2026"
              align="center"
              className="mb-16"
            />
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              <div>
                <h2 className="text-2xl font-bold mb-4">1. General Policy</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  At Best Odds Corp, we strive to deliver exceptional service and results. Due to the custom nature of our creative and production services, refunds are handled on a case-by-case basis.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">2. Deposit Refunds</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  Project deposits are generally non-refundable once work has commenced. However, deposits may be refunded under the following circumstances:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li>Project cancellation before work begins (within 48 hours of deposit)</li>
                  <li>Inability to deliver services due to circumstances on our end</li>
                  <li>Mutual agreement between client and Best Odds Corp</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">3. Project Cancellation</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  If a client wishes to cancel a project after work has begun, they are responsible for payment of all work completed up to the cancellation date. This includes:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mt-4">
                  <li>Strategy and planning work</li>
                  <li>Creative development and concepting</li>
                  <li>Production time and resources</li>
                  <li>Third-party costs incurred</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">4. Dissatisfaction with Work</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  We are committed to client satisfaction. If you are not satisfied with the work delivered:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4 mt-4">
                  <li>Contact us within 7 days of delivery to discuss concerns</li>
                  <li>We will work with you to address issues through revisions</li>
                  <li>Revisions are subject to the terms outlined in your project agreement</li>
                  <li>Refunds for completed work are evaluated on a case-by-case basis</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">5. Refund Process</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  If a refund is approved:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li>Refunds will be processed within 10 business days</li>
                  <li>Refunds will be issued to the original payment method</li>
                  <li>Partial refunds may be issued based on work completed</li>
                  <li>Third-party costs are non-refundable</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">6. Non-Refundable Items</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                  The following are non-refundable:
                </p>
                <ul className="list-disc list-inside text-body-sm text-muted-foreground space-y-2 ml-4">
                  <li>Third-party costs (stock footage, music licenses, software, etc.)</li>
                  <li>Completed and delivered work that meets agreed specifications</li>
                  <li>Consultation and strategy sessions</li>
                  <li>Rush fees and expedited service charges</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">7. Dispute Resolution</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  We encourage clients to contact us directly to resolve any issues. If a dispute cannot be resolved through direct communication, both parties agree to pursue mediation before legal action.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">8. Changes to This Policy</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to our website.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold mb-4">9. Contact Us</h2>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  For questions about refunds or to request a refund, please contact us at hello@bestoddscorp.com or (702) 546-6337.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}